"use client"

import * as React from 'react'
import Link from 'next/link'
import type { ColumnDef } from '@tanstack/react-table'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { Button } from '@open-mercato/ui/primitives/button'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type ShipmentLeg = {
  id: string
  sequenceNumber: number
  mode: string
  transportType?: string | null
  originLocationId: string
  destinationLocationId: string
  distanceKm?: string | null
  carrierId?: string | null
  price?: string | null
  currency: string
  status: string
  estimatedDeparture?: string | null
  estimatedArrival?: string | null
  actualDeparture?: string | null
  actualArrival?: string | null
  createdAt: string
}

type ShipmentDetail = {
  id: string
  shipmentNumber: string
  status: string
  shipperId: string
  consigneeId?: string | null
  cargoSegment: string
  weightTons: string
  volumeM3?: string | null
  originLocationId: string
  destinationLocationId: string
  pickupEarliest: string
  pickupLatest: string
  deliveryDeadline?: string | null
  modeConstraint: string
  marketType: string
  currency: string
  visibility: string
  publishedAt?: string | null
  bookedAt?: string | null
  deliveredAt?: string | null
  cancelledAt?: string | null
  cancellationReason?: string | null
  notes?: string | null
  tags?: string[] | null
  createdByUserId: string
  createdAt: string
  updatedAt: string
  legs: ShipmentLeg[]
}

type DetailProps = { params: { id: string } }

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  published: 'bg-blue-100 text-blue-700',
  matching: 'bg-yellow-100 text-yellow-700',
  quoted: 'bg-purple-100 text-purple-700',
  booked: 'bg-green-100 text-green-700',
  executing: 'bg-orange-100 text-orange-700',
  delivered: 'bg-emerald-100 text-emerald-700',
  settled: 'bg-teal-100 text-teal-700',
  cancelled: 'bg-red-100 text-red-700',
}

const legStatusColors: Record<string, string> = {
  planned: 'bg-gray-100 text-gray-700',
  carrier_assigned: 'bg-blue-100 text-blue-700',
  ready: 'bg-indigo-100 text-indigo-700',
  in_transit: 'bg-orange-100 text-orange-700',
  at_handoff: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
}

export default function ShipmentDetailPage({ params }: DetailProps) {
  const t = useT()
  const shipmentId = params.id
  const [shipment, setShipment] = React.useState<ShipmentDetail | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [actionInProgress, setActionInProgress] = React.useState(false)
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation, retryLastMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: `freight-marketplace-shipment:${shipmentId}`,
    blockedMessage: t('marketplace.pages.detail.actionBlocked', 'Action blocked.'),
  })
  const mutationContext = React.useMemo(() => ({
    shipmentId,
    resourceKind: 'freight:shipment',
    resourceId: shipmentId,
    data: shipment,
    retryLastMutation,
  }), [retryLastMutation, shipment, shipmentId])
  const legColumns = React.useMemo<ColumnDef<ShipmentLeg>[]>(() => [
    {
      accessorKey: 'sequenceNumber',
      header: '#',
      meta: { maxWidth: '4rem' },
    },
    {
      accessorKey: 'mode',
      header: t('marketplace.fields.mode', 'Mode'),
      cell: ({ row }) => t(`marketplace.transportMode.${row.original.mode}`, row.original.mode),
      meta: { maxWidth: '8rem' },
    },
    {
      accessorKey: 'originLocationId',
      header: t('marketplace.fields.originLocationId', 'Origin'),
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.originLocationId.substring(0, 8)}...</span>,
      meta: { maxWidth: '9rem' },
    },
    {
      accessorKey: 'destinationLocationId',
      header: t('marketplace.fields.destinationLocationId', 'Destination'),
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.destinationLocationId.substring(0, 8)}...</span>,
      meta: { maxWidth: '9rem' },
    },
    {
      accessorKey: 'status',
      header: t('marketplace.fields.status', 'Status'),
      cell: ({ row }) => (
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${legStatusColors[row.original.status] ?? 'bg-gray-100 text-gray-700'}`}>
          {t(`marketplace.legStatus.${row.original.status}`, row.original.status)}
        </span>
      ),
      meta: { maxWidth: '10rem' },
    },
    {
      accessorKey: 'price',
      header: t('marketplace.fields.price', 'Price'),
      cell: ({ row }) => row.original.price ? `${row.original.price} ${row.original.currency}` : '-',
      meta: { maxWidth: '9rem' },
    },
  ], [t])

  const loadShipment = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiCall<ShipmentDetail>(`/api/freight/marketplace/shipments/${shipmentId}`)
      if (!response.ok || !response.result) {
        throw new Error(t('marketplace.pages.detail.loadError', 'Failed to load shipment.'))
      }
      setShipment(response.result)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('marketplace.pages.detail.loadError', 'Failed to load shipment.'))
    } finally {
      setLoading(false)
    }
  }, [shipmentId, t])

  React.useEffect(() => {
    void loadShipment()
  }, [loadShipment])

  const executeAction = async (action: string, confirmTitle: string, confirmText: string) => {
    const confirmed = await confirm({
      title: confirmTitle,
      text: confirmText,
      confirmText: t('marketplace.pages.detail.confirm', 'Confirm'),
    })
    if (!confirmed) return
    setActionInProgress(true)
    try {
      await runMutation({
        mutationPayload: { id: shipmentId, action },
        context: mutationContext,
        operation: () => apiCallOrThrow(`/api/freight/marketplace/shipments/${shipmentId}/${action}`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({}),
        }, {
          errorMessage: t('marketplace.pages.detail.actionError', 'Action failed.'),
        }),
      })
      flash(t('marketplace.pages.detail.actionSuccess', 'Action completed.'), 'success')
      await loadShipment()
    } finally {
      setActionInProgress(false)
    }
  }

  const canPublish = shipment?.status === 'draft' || shipment?.status === 'matching'
  const canBook = shipment?.status === 'quoted' || shipment?.status === 'published'
  const canDeliver = shipment?.status === 'executing'
  const canCancel = shipment?.status !== 'delivered' && shipment?.status !== 'settled' && shipment?.status !== 'cancelled'

  return (
    <Page>
      <PageBody>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-muted-foreground">
                {shipment ? t(`marketplace.cargoSegment.${shipment.cargoSegment}`, shipment.cargoSegment) : ''}
              </div>
              <h1 className="text-2xl font-semibold">
                {shipment?.shipmentNumber ?? t('marketplace.pages.detail.title', 'Shipment detail')}
              </h1>
              {shipment ? (
                <span className={`mt-1 inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[shipment.status] ?? 'bg-gray-100 text-gray-700'}`}>
                  {t(`marketplace.status.${shipment.status}`, shipment.status)}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {shipment && canPublish ? (
                <Button
                  type="button"
                  variant="default"
                  disabled={actionInProgress}
                  onClick={() => executeAction(
                    'publish',
                    t('marketplace.pages.detail.publishTitle', 'Publish shipment'),
                    t('marketplace.pages.detail.publishConfirm', 'This will make the shipment visible on the marketplace.'),
                  )}
                >
                  {t('marketplace.actions.publish', 'Publish')}
                </Button>
              ) : null}
              {shipment && canBook ? (
                <Button
                  type="button"
                  variant="default"
                  disabled={actionInProgress}
                  onClick={() => executeAction(
                    'book',
                    t('marketplace.pages.detail.bookTitle', 'Book shipment'),
                    t('marketplace.pages.detail.bookConfirm', 'This will mark the shipment as booked.'),
                  )}
                >
                  {t('marketplace.actions.book', 'Book')}
                </Button>
              ) : null}
              {shipment && canDeliver ? (
                <Button
                  type="button"
                  variant="default"
                  disabled={actionInProgress}
                  onClick={() => executeAction(
                    'deliver',
                    t('marketplace.pages.detail.deliverTitle', 'Deliver shipment'),
                    t('marketplace.pages.detail.deliverConfirm', 'This will mark the shipment as delivered.'),
                  )}
                >
                  {t('marketplace.actions.deliver', 'Deliver')}
                </Button>
              ) : null}
              {shipment && canCancel ? (
                <Button
                  type="button"
                  variant="destructive"
                  disabled={actionInProgress}
                  onClick={() => executeAction(
                    'cancel',
                    t('marketplace.pages.detail.cancelTitle', 'Cancel shipment'),
                    t('marketplace.pages.detail.cancelConfirm', 'This will cancel the shipment. This action cannot be undone.'),
                  )}
                >
                  {t('marketplace.actions.cancel', 'Cancel')}
                </Button>
              ) : null}
              <Button asChild variant="outline">
                <Link href="/backend/marketplace">{t('marketplace.pages.detail.back', 'Back to shipments')}</Link>
              </Button>
            </div>
          </div>

          {loading ? <LoadingMessage label={t('marketplace.pages.detail.loading', 'Loading shipment...')} /> : null}
          {error ? <ErrorMessage label={error} /> : null}

          {!loading && !error && shipment ? (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('marketplace.fields.marketType', 'Market type')}</div>
                  <div className="mt-2 text-lg font-medium">{t(`marketplace.marketType.${shipment.marketType}`, shipment.marketType)}</div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('marketplace.fields.modeConstraint', 'Mode')}</div>
                  <div className="mt-2 text-lg font-medium">{t(`marketplace.modeConstraint.${shipment.modeConstraint}`, shipment.modeConstraint)}</div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('marketplace.fields.weightTons', 'Weight')}</div>
                  <div className="mt-2 text-lg font-medium">{shipment.weightTons} t</div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('marketplace.fields.currency', 'Currency')}</div>
                  <div className="mt-2 text-lg font-medium">{shipment.currency}</div>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-2">
                <div className="rounded-xl border p-4">
                  <h2 className="text-lg font-medium">{t('marketplace.pages.detail.details', 'Shipment details')}</h2>
                  <div className="mt-4 grid gap-3 text-sm text-muted-foreground md:grid-cols-2">
                    <div>
                      <span className="font-medium text-foreground">{t('marketplace.fields.pickupEarliest', 'Pickup earliest')}:</span>{' '}
                      {new Date(shipment.pickupEarliest).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium text-foreground">{t('marketplace.fields.pickupLatest', 'Pickup latest')}:</span>{' '}
                      {new Date(shipment.pickupLatest).toLocaleString()}
                    </div>
                    {shipment.deliveryDeadline ? (
                      <div>
                        <span className="font-medium text-foreground">{t('marketplace.fields.deliveryDeadline', 'Delivery deadline')}:</span>{' '}
                        {new Date(shipment.deliveryDeadline).toLocaleString()}
                      </div>
                    ) : null}
                    <div>
                      <span className="font-medium text-foreground">{t('marketplace.fields.visibility', 'Visibility')}:</span>{' '}
                      {t(`marketplace.visibility.${shipment.visibility}`, shipment.visibility)}
                    </div>
                    {shipment.notes ? (
                      <div className="md:col-span-2">
                        <span className="font-medium text-foreground">{t('marketplace.fields.notes', 'Notes')}:</span>{' '}
                        {shipment.notes}
                      </div>
                    ) : null}
                    {shipment.cancellationReason ? (
                      <div className="md:col-span-2">
                        <span className="font-medium text-foreground">{t('marketplace.fields.cancellationReason', 'Cancellation reason')}:</span>{' '}
                        {shipment.cancellationReason}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-xl border p-4">
                  <h2 className="text-lg font-medium">{t('marketplace.pages.detail.timeline', 'Timeline')}</h2>
                  <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                    <div>{t('marketplace.fields.createdAt', 'Created')}: {new Date(shipment.createdAt).toLocaleString()}</div>
                    {shipment.publishedAt ? <div>{t('marketplace.fields.publishedAt', 'Published')}: {new Date(shipment.publishedAt).toLocaleString()}</div> : null}
                    {shipment.bookedAt ? <div>{t('marketplace.fields.bookedAt', 'Booked')}: {new Date(shipment.bookedAt).toLocaleString()}</div> : null}
                    {shipment.deliveredAt ? <div>{t('marketplace.fields.deliveredAt', 'Delivered')}: {new Date(shipment.deliveredAt).toLocaleString()}</div> : null}
                    {shipment.cancelledAt ? <div>{t('marketplace.fields.cancelledAt', 'Cancelled')}: {new Date(shipment.cancelledAt).toLocaleString()}</div> : null}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border p-4">
                <h2 className="text-lg font-medium">{t('marketplace.pages.detail.legs', 'Legs')}</h2>
                {shipment.legs.length === 0 ? (
                  <div className="mt-4 text-sm text-muted-foreground">
                    {t('marketplace.pages.detail.noLegs', 'No legs defined for this shipment yet.')}
                  </div>
                ) : (
                  <div className="mt-4">
                    <DataTable<ShipmentLeg>
                      data={shipment.legs}
                      columns={legColumns}
                      embedded
                      injectionSpotId="data-table:freight.marketplace.shipment_legs"
                      replacementHandle="data-table:freight.marketplace.shipment_legs"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
        {ConfirmDialogElement}
      </PageBody>
    </Page>
  )
}
